<template>
  <div class="h-full flex flex-col bg-white rounded-lg shadow">
    <!-- Loading overlay -->
    <div
      v-if="loading"
      class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50"
    >
      <div class="text-lg text-gray-600">Loading...</div>
    </div>

    <!-- Stats Bar -->
    <div class="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
      <div class="flex items-center justify-between flex-wrap gap-4">
        <div class="flex items-center gap-6 text-sm">
          <div>
            <span class="text-gray-500">Total:</span>
            <span class="ml-1 font-semibold">{{ stats.total }}</span>
          </div>
          <div>
            <span class="text-gray-500">Validated:</span>
            <span class="ml-1 font-semibold text-green-600">{{ stats.validated }}</span>
          </div>
          <div>
            <span class="text-gray-500">Awaiting:</span>
            <span class="ml-1 font-semibold text-yellow-600">{{ stats.awaiting_validation }}</span>
          </div>
          <div>
            <span class="text-gray-500">Churches:</span>
            <span class="ml-1 font-semibold">{{ stats.churches }}</span>
          </div>
          <div>
            <span class="text-gray-500">People:</span>
            <span class="ml-1 font-semibold">{{ stats.people }}</span>
          </div>
        </div>

        <!-- Bulk Action Buttons -->
        <div v-if="selectedUuids.length > 0" class="flex items-center gap-2">
          <span class="text-sm text-gray-500">{{ selectedUuids.length }} selected</span>
          <button
            @click="bulkValidate"
            :disabled="actionLoading"
            class="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Validate Selected
          </button>
          <button
            @click="bulkInclude"
            :disabled="actionLoading"
            class="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            Include Selected
          </button>
        </div>
      </div>
    </div>

    <!-- Filter Slider -->
    <div class="px-4 py-2 border-b border-gray-200">
      <MrdSliderFilter :initial-days="filterDays" @update:days="updateFilter" />
    </div>

    <!-- Keyboard Shortcuts -->
    <div class="px-4 py-2 border-b border-gray-200 bg-gray-50">
      <div class="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
        <span class="font-medium text-gray-600">Shortcuts:</span>
        <span><kbd class="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-gray-700 font-mono">V</kbd> Validate</span>
        <span><kbd class="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-gray-700 font-mono">I</kbd> Include</span>
        <span><kbd class="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-gray-700 font-mono">&uarr;</kbd>/<kbd class="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-gray-700 font-mono">&darr;</kbd> Navigate</span>
        <span><kbd class="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-gray-700 font-mono">Esc</kbd> Close panel</span>
        <span><kbd class="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-gray-700 font-mono">Ctrl</kbd>+<kbd class="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-gray-700 font-mono">A</kbd> Select all</span>
        <span><kbd class="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-gray-700 font-mono">Space</kbd> Toggle checkbox</span>
      </div>
    </div>

    <!-- Main Content: Table always full width; detail panel is a fixed overlay -->
    <div class="flex-1 flex overflow-hidden">
      <div class="flex-1 overflow-auto w-full">
        <MrdValidationTable
          ref="tableRef"
          :mrds="mrds"
          :selected-uuids="selectedUuids"
          :selected-mrd-uuid="selectedMrd?.uuid"
          @select-row="selectMrd"
          @toggle-selection="toggleSelection"
          @select-all="selectAll"
          @deselect-all="deselectAll"
        />
      </div>
    </div>

    <!-- Detail Panel as fixed overlay teleported to body so it isn't constrained by parent -->
    <Teleport to="body">
      <transition name="overlay-backdrop">
        <div
          v-if="selectedMrd"
          class="fixed inset-0 bg-black bg-opacity-30 z-40"
          @click="onBackdropClick"
        ></div>
      </transition>

      <transition name="slide-panel">
        <div
          v-if="selectedMrd"
          class="fixed top-0 right-0 h-full w-full md:w-1/2 lg:w-2/5 xl:w-1/3 bg-white border-l border-gray-200 shadow-2xl z-50 overflow-hidden flex flex-col"
          role="dialog"
          aria-modal="true"
        >
          <MrdDetailPanel
            :mrd="selectedMrd"
            :previous-mrds="previousMrds"
            :is-selected="selectedUuids.includes(selectedMrd.uuid)"
            :loading="actionLoading"
            @close="closeDetailPanel"
            @validate="validateMrd"
            @include="includeMrd"
            @unvalidate="unvalidateMrd"
            @unexclude="unexcludeMrd"
            @update-mrd="updateMrd"
            @toggle-selection="toggleSelection"
            @navigate-previous="navigateToPrevious"
            @navigate-next="navigateToNext"
          />
        </div>
      </transition>
    </Teleport>

    <!-- Error/Success Messages -->
    <div
      v-if="message"
      :class="[
        'px-4 py-3 text-sm',
        message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
      ]"
    >
      {{ message.text }}
      <button @click="message = null" class="ml-2 underline">Dismiss</button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import MrdSliderFilter from './MrdSliderFilter.vue'
import MrdValidationTable from './MrdValidationTable.vue'
import MrdDetailPanel from './MrdDetailPanel.vue'

const props = defineProps({
  trainingCenterReportUuid: {
    type: String,
    required: true
  },
  trainingCenterId: {
    type: Number,
    required: true
  }
})

// State
const loading = ref(false)
const actionLoading = ref(false)
// Monotonic counter so out-of-order detail-fetch responses can be discarded.
let detailFetchSeq = 0
const mrds = ref([])
const stats = ref({
  total: 0,
  validated: 0,
  awaiting_validation: 0,
  churches: 0,
  people: 0
})
const filterDays = ref(0)
const selectedMrd = ref(null)
const previousMrds = ref([])
const selectedUuids = ref([])
const message = ref(null)
const tableRef = ref(null)

// Computed
const csrfToken = computed(() => {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
})

// Methods
const fetchMrds = async () => {
  loading.value = true
  try {
    const params = new URLSearchParams({
      training_center_id: props.trainingCenterId,
      training_center_report_uuid: props.trainingCenterReportUuid
    })
    if (filterDays.value > 0) {
      params.append('days', filterDays.value)
    }

    const response = await fetch(`/api/v1/ministry_report_details?${params}`, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken.value
      }
    })

    if (!response.ok) throw new Error('Failed to fetch MRDs')

    const data = await response.json()
    mrds.value = data.ministry_report_details
    stats.value = data.stats

    // Drop any selected UUIDs that no longer appear in the refreshed list so
    // bulk action counts and requests don't reference stale rows.
    const visibleUuids = new Set(mrds.value.map(m => m.uuid))
    selectedUuids.value = selectedUuids.value.filter(u => visibleUuids.has(u))
  } catch (error) {
    console.error('Error fetching MRDs:', error)
    showMessage('error', 'Failed to load ministry report details')
  } finally {
    loading.value = false
  }
}

const fetchMrdDetails = async (uuid) => {
  // Capture the sequence number for this request. Any later call (or close)
  // increments the counter, so we can detect when a stale response arrives
  // after the user has already moved on and discard it.
  const seq = ++detailFetchSeq
  try {
    const params = new URLSearchParams({
      training_center_report_uuid: props.trainingCenterReportUuid
    })
    const response = await fetch(`/api/v1/ministry_report_details/${uuid}?${params}`, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken.value
      }
    })

    if (!response.ok) throw new Error('Failed to fetch MRD details')

    const data = await response.json()
    if (seq !== detailFetchSeq) return // stale response — drop it
    selectedMrd.value = data.ministry_report_detail
    previousMrds.value = data.previous_mrds || []
  } catch (error) {
    if (seq !== detailFetchSeq) return
    console.error('Error fetching MRD details:', error)
    showMessage('error', 'Failed to load MRD details')
  }
}

const selectMrd = (mrd) => {
  fetchMrdDetails(mrd.uuid)
}

const closeDetailPanel = () => {
  selectedMrd.value = null
  previousMrds.value = []
  // Drop any in-flight detail-fetch result so it can't reopen the panel.
  detailFetchSeq += 1
}

// Backdrop click should only close when no action is mid-flight; otherwise the
// user could lose feedback from the in-progress request.
const onBackdropClick = () => {
  if (actionLoading.value) return
  closeDetailPanel()
}

const updateFilter = (days) => {
  filterDays.value = days
  fetchMrds()
}

const toggleSelection = (uuid) => {
  const index = selectedUuids.value.indexOf(uuid)
  if (index === -1) {
    selectedUuids.value.push(uuid)
  } else {
    selectedUuids.value.splice(index, 1)
  }
}

const selectAll = () => {
  selectedUuids.value = mrds.value.map(m => m.uuid)
}

const deselectAll = () => {
  selectedUuids.value = []
}

const validateMrd = async (mrd) => {
  actionLoading.value = true
  try {
    const response = await fetch(`/api/v1/ministry_report_details/${mrd.uuid}/validate`, {
      method: 'PATCH',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken.value
      }
    })

    const data = await response.json()

    if (data.success) {
      showMessage('success', 'MRD validated successfully')
      await fetchMrds()
      autoAdvance()
    } else {
      showMessage('error', data.errors?.join(', ') || 'Validation failed')
    }
  } catch (error) {
    console.error('Error validating MRD:', error)
    showMessage('error', 'Failed to validate MRD')
  } finally {
    actionLoading.value = false
  }
}

const includeMrd = async (mrd) => {
  actionLoading.value = true
  try {
    const response = await fetch(`/api/v1/ministry_report_details/${mrd.uuid}/include`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken.value
      },
      body: JSON.stringify({
        training_center_report_uuid: props.trainingCenterReportUuid
      })
    })

    const data = await response.json()

    if (data.success) {
      showMessage('success', 'MRD included successfully')
      if (data.tcr_metrics) {
        emitTcrMetricsUpdate(data.tcr_metrics)
      }
      await fetchMrds()
      autoAdvance()
    } else {
      showMessage('error', data.errors?.join(', ') || 'Inclusion failed')
    }
  } catch (error) {
    console.error('Error including MRD:', error)
    showMessage('error', 'Failed to include MRD')
  } finally {
    actionLoading.value = false
  }
}

const unvalidateMrd = async (mrd) => {
  actionLoading.value = true
  try {
    const response = await fetch(`/api/v1/ministry_report_details/${mrd.uuid}/unvalidate`, {
      method: 'PATCH',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken.value
      }
    })

    const data = await response.json()

    if (data.success) {
      showMessage('success', 'MRD un-validated successfully')
      if (data.tcr_metrics) {
        emitTcrMetricsUpdate(data.tcr_metrics)
      }
      await fetchMrds()
      // Re-fetch the detail panel to reflect the new state
      if (selectedMrd.value?.uuid === mrd.uuid) {
        fetchMrdDetails(mrd.uuid)
      }
    } else {
      showMessage('error', data.errors?.join(', ') || 'Un-validation failed')
    }
  } catch (error) {
    console.error('Error un-validating MRD:', error)
    showMessage('error', 'Failed to un-validate MRD')
  } finally {
    actionLoading.value = false
  }
}

const unexcludeMrd = async (mrd) => {
  actionLoading.value = true
  try {
    const response = await fetch(`/api/v1/ministry_report_details/${mrd.uuid}/unexclude`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken.value
      },
      body: JSON.stringify({
        training_center_report_uuid: props.trainingCenterReportUuid
      })
    })

    const data = await response.json()

    if (data.success) {
      showMessage('success', 'MRD un-included successfully')
      if (data.tcr_metrics) {
        emitTcrMetricsUpdate(data.tcr_metrics)
      }
      await fetchMrds()
      if (selectedMrd.value?.uuid === mrd.uuid) {
        fetchMrdDetails(mrd.uuid)
      }
    } else {
      showMessage('error', data.errors?.join(', ') || 'Un-include failed')
    }
  } catch (error) {
    console.error('Error un-including MRD:', error)
    showMessage('error', 'Failed to un-include MRD')
  } finally {
    actionLoading.value = false
  }
}

const updateMrd = async (payload) => {
  actionLoading.value = true
  try {
    const { uuid, ...attrs } = payload
    const response = await fetch(`/api/v1/ministry_report_details/${uuid}`, {
      method: 'PATCH',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken.value
      },
      body: JSON.stringify(attrs)
    })

    const data = await response.json()

    if (data.success) {
      showMessage('success', 'MRD updated successfully')
      if (data.tcr_metrics) {
        emitTcrMetricsUpdate(data.tcr_metrics)
      }
      await fetchMrds()
      if (selectedMrd.value?.uuid === uuid) {
        // Await the detail refresh so actionLoading stays true until selectedMrd
        // reflects the new values — otherwise MrdDetailPanel's Save button would
        // briefly re-enable between `loading=false` and the updated props arriving.
        await fetchMrdDetails(uuid)
      }
    } else {
      showMessage('error', data.errors?.join(', ') || 'Update failed')
    }
  } catch (error) {
    console.error('Error updating MRD:', error)
    showMessage('error', 'Failed to update MRD')
  } finally {
    actionLoading.value = false
  }
}

const bulkValidate = async () => {
  if (selectedUuids.value.length === 0) return

  actionLoading.value = true
  try {
    const response = await fetch('/api/v1/ministry_report_details/bulk_actions', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken.value
      },
      body: JSON.stringify({
        uuids: selectedUuids.value,
        action_type: 'validate'
      })
    })

    const data = await response.json()

    if (data.success || data.partial_success) {
      const msg = data.partial_success
        ? `Validated ${data.success_count} of ${data.success_count + data.failure_count} MRDs`
        : `Validated ${data.count} MRDs`
      showMessage('success', msg)
      selectedUuids.value = []
      await fetchMrds()
    } else {
      showMessage('error', 'Bulk validation failed')
    }
  } catch (error) {
    console.error('Error bulk validating:', error)
    showMessage('error', 'Failed to validate MRDs')
  } finally {
    actionLoading.value = false
  }
}

const bulkInclude = async () => {
  if (selectedUuids.value.length === 0) return

  actionLoading.value = true
  try {
    const response = await fetch('/api/v1/ministry_report_details/bulk_actions', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken.value
      },
      body: JSON.stringify({
        uuids: selectedUuids.value,
        action_type: 'include',
        training_center_report_uuid: props.trainingCenterReportUuid
      })
    })

    const data = await response.json()

    if (data.success || data.partial_success) {
      const msg = data.partial_success
        ? `Included ${data.success_count} of ${data.success_count + data.failure_count} MRDs`
        : `Included ${data.count} MRDs`
      showMessage('success', msg)
      if (data.tcr_metrics) {
        emitTcrMetricsUpdate(data.tcr_metrics)
      }
      selectedUuids.value = []
      await fetchMrds()
    } else {
      showMessage('error', 'Bulk inclusion failed')
    }
  } catch (error) {
    console.error('Error bulk including:', error)
    showMessage('error', 'Failed to include MRDs')
  } finally {
    actionLoading.value = false
  }
}

const autoAdvance = () => {
  // Find next unvalidated MRD
  const nextMrd = mrds.value.find(m => m.state !== 'validated')
  if (nextMrd) {
    fetchMrdDetails(nextMrd.uuid)
    tableRef.value?.focusRow(nextMrd.uuid)
  } else {
    closeDetailPanel()
    showMessage('success', 'All MRDs have been validated!')
  }
}

const navigateToPrevious = () => {
  if (!selectedMrd.value || mrds.value.length === 0) return
  const currentIndex = mrds.value.findIndex(m => m.uuid === selectedMrd.value.uuid)
  if (currentIndex > 0) {
    const prevMrd = mrds.value[currentIndex - 1]
    fetchMrdDetails(prevMrd.uuid)
    tableRef.value?.focusRow(prevMrd.uuid)
  }
}

const navigateToNext = () => {
  if (!selectedMrd.value || mrds.value.length === 0) return
  const currentIndex = mrds.value.findIndex(m => m.uuid === selectedMrd.value.uuid)
  if (currentIndex < mrds.value.length - 1) {
    const nextMrd = mrds.value[currentIndex + 1]
    fetchMrdDetails(nextMrd.uuid)
    tableRef.value?.focusRow(nextMrd.uuid)
  }
}

const emitTcrMetricsUpdate = (metrics) => {
  // Emit a custom DOM event so the parent page can update TCR metrics display
  const event = new CustomEvent('tcr-metrics-updated', {
    detail: metrics,
    bubbles: true
  })
  document.dispatchEvent(event)
}

const showMessage = (type, text) => {
  message.value = { type, text }
  setTimeout(() => {
    if (message.value?.text === text) {
      message.value = null
    }
  }, 5000)
}

// Keyboard shortcuts
const handleKeydown = (event) => {
  // Don't handle if typing in an input
  if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return

  switch (event.key.toLowerCase()) {
    case 'v':
      if (selectedMrd.value && selectedMrd.value.state !== 'validated') {
        event.preventDefault()
        validateMrd(selectedMrd.value)
      }
      break
    case 'i':
      if (selectedMrd.value && !selectedMrd.value.included_in_tcr) {
        event.preventDefault()
        includeMrd(selectedMrd.value)
      }
      break
    case 'a':
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault()
        selectAll()
      }
      break
    case 'arrowup':
      if (selectedMrd.value) {
        event.preventDefault()
        navigateToPrevious()
      }
      break
    case 'arrowdown':
      if (selectedMrd.value) {
        event.preventDefault()
        navigateToNext()
      }
      break
    case 'escape':
      event.preventDefault()
      if (selectedMrd.value) {
        closeDetailPanel()
      } else {
        deselectAll()
      }
      break
  }
}

// Lock body scroll while overlay is open
watch(selectedMrd, (newVal) => {
  if (typeof document === 'undefined') return
  if (newVal) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

// Handle requests from outside Vue (e.g., the Detail Records table) to open
// the MRD panel for a specific MRD UUID.
const handleOpenMrdPanel = (event) => {
  const uuid = event.detail?.uuid
  if (uuid) {
    fetchMrdDetails(uuid)
  }
}

// Lifecycle
onMounted(() => {
  fetchMrds()
  if (typeof document !== 'undefined') {
    document.addEventListener('keydown', handleKeydown)
    document.addEventListener('open-mrd-panel', handleOpenMrdPanel)
  }
})

onUnmounted(() => {
  if (typeof document !== 'undefined') {
    document.body.style.overflow = ''
    document.removeEventListener('keydown', handleKeydown)
    document.removeEventListener('open-mrd-panel', handleOpenMrdPanel)
  }
})
</script>

<style scoped>
.slide-panel-enter-active,
.slide-panel-leave-active {
  transition: transform 0.3s ease;
}

.slide-panel-enter-from,
.slide-panel-leave-to {
  transform: translateX(100%);
}

.overlay-backdrop-enter-active,
.overlay-backdrop-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-backdrop-enter-from,
.overlay-backdrop-leave-to {
  opacity: 0;
}
</style>
